import { ref } from 'vue'

/**
 * Global navigation progress state. Provides a top-of-page progress bar
 * indicator while a route transition is in flight (covers async route
 * component chunk downloads and any beforeEach awaits, so there is visible
 * feedback between click and view mount even when the new view's own
 * loading state hasn't appeared yet).
 *
 * Module-level singleton refs — every caller of useNavigationProgress()
 * shares the same state, so the bar is rendered once in App.vue and
 * driven from router lifecycle hooks.
 */

const SHOW_DELAY_MS = 80
const HIDE_AFTER_DONE_MS = 220
const TRICKLE_INTERVAL_MS = 200
const TRICKLE_MAX = 0.88
const RESET_AFTER_HIDE_MS = 200

const progress = ref(0)
const isVisible = ref(false)

let isActive = false
let showTimer: ReturnType<typeof setTimeout> | null = null
let trickleTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null
let resetTimer: ReturnType<typeof setTimeout> | null = null

function clearShowTimer() {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
}

function clearTrickleTimer() {
  if (trickleTimer) {
    clearTimeout(trickleTimer)
    trickleTimer = null
  }
}

function clearHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function clearResetTimer() {
  if (resetTimer) {
    clearTimeout(resetTimer)
    resetTimer = null
  }
}

function trickle() {
  const remaining = TRICKLE_MAX - progress.value
  if (remaining <= 0) {
    trickleTimer = setTimeout(trickle, TRICKLE_INTERVAL_MS)
    return
  }
  const inc = Math.max(0.005, remaining * 0.08)
  progress.value = Math.min(TRICKLE_MAX, progress.value + inc)
  trickleTimer = setTimeout(trickle, TRICKLE_INTERVAL_MS)
}

function showBar() {
  clearHideTimer()
  clearResetTimer()
  isVisible.value = true
  if (progress.value === 0) {
    progress.value = 0.08
  }
  clearTrickleTimer()
  trickleTimer = setTimeout(trickle, TRICKLE_INTERVAL_MS)
}

/**
 * Begin (or extend) a navigation. Idempotent — calling multiple times in
 * a row (e.g. through a redirect chain) keeps the bar visible without
 * resetting progress. The bar appears after a short debounce so instant
 * transitions don't flash a sliver of bar on screen.
 */
function start() {
  isActive = true
  clearHideTimer()
  clearResetTimer()
  if (isVisible.value) return
  if (showTimer) return
  showTimer = setTimeout(() => {
    showTimer = null
    if (isActive) showBar()
  }, SHOW_DELAY_MS)
}

/**
 * Mark the active navigation as complete. The bar jumps to 100% and fades
 * out. Calling done() when no navigation is active is a no-op.
 */
function done() {
  if (!isActive) return
  isActive = false

  clearShowTimer()
  clearTrickleTimer()

  // If the bar never became visible (instant nav), just reset silently.
  if (!isVisible.value) {
    progress.value = 0
    return
  }

  progress.value = 1
  clearHideTimer()
  hideTimer = setTimeout(() => {
    hideTimer = null
    isVisible.value = false
    clearResetTimer()
    resetTimer = setTimeout(() => {
      resetTimer = null
      // Only reset if no new navigation started during the hide animation.
      if (!isActive && !isVisible.value) {
        progress.value = 0
      }
    }, RESET_AFTER_HIDE_MS)
  }, HIDE_AFTER_DONE_MS)
}

/** Resets all state. Useful in tests. */
function $reset() {
  clearShowTimer()
  clearTrickleTimer()
  clearHideTimer()
  clearResetTimer()
  isActive = false
  progress.value = 0
  isVisible.value = false
}

export function useNavigationProgress() {
  return {
    progress,
    isVisible,
    start,
    done,
    $reset,
  }
}
